<template>
  <div class="xrev">
    <div class="xrev__inner">
      <button type="button" class="xback" @click="goBack">
        <i class="mdi mdi-arrow-left" aria-hidden="true"></i>
        {{ $t("common.back") }}
      </button>

      <page-skeleton v-if="loading" variant="sheet" class="mt-4" />

      <p v-else-if="error" class="xstate xstate__text">{{ $t(error) }}</p>

      <template v-else-if="view">
        <header class="xhead">
          <p class="xhead__student">{{ view.student && view.student.name }}</p>
          <h1 class="xhead__title">
            {{ view.exercise.setTitle }}
            <span class="xhead__ex">{{
              $t("studentExercises.exerciseN", { n: view.exercise.order + 1 })
            }}</span>
          </h1>
          <p v-if="view.exercise.prompt" class="xhead__prompt">
            {{ view.exercise.prompt }}
          </p>
        </header>

        <section v-if="!finished" class="xwait">
          <p class="xwait__title">
            {{ $t("review.notFinishedTitle") }}
          </p>
          <p class="xwait__text">
            {{
              $t("review.notFinishedText", {
                date: formatDate(view.attempt.startedAt),
              })
            }}
          </p>
        </section>

        <template v-else>
          <section class="xresult">
            <score-mark
              :correct="view.attempt.correctItems"
              :total="view.attempt.totalItems"
              small
            />
            <div>
              <p class="xresult__lead">
                {{
                  $t("review.lead", {
                    correct: view.attempt.correctItems,
                    total: view.attempt.totalItems,
                    percent: view.attempt.percentage,
                  })
                }}
              </p>
              <p class="xresult__hint">
                {{
                  $t("review.finishedOn", {
                    date: formatDate(view.attempt.finishedAt),
                  })
                }}
                <template v-if="toleranceCount">
                  {{
                    $tc("review.tolerance", toleranceCount, {
                      count: toleranceCount,
                    })
                  }}
                </template>
                {{ $t("review.disagree") }}
              </p>
            </div>
            <!-- Retorno de 16/10: a tentativa sai das pendencias quando o
                 professor marca como revisada (ou comenta). O aluno ve. -->
            <div class="xreviewed">
              <template v-if="view.attempt.reviewedAt">
                <span class="xreviewed__done">
                  <i class="mdi mdi-check-circle" aria-hidden="true"></i>
                  {{
                    $t("review.reviewedOn", {
                      date: formatDate(view.attempt.reviewedAt),
                    })
                  }}
                </span>
                <button
                  type="button"
                  class="xlink"
                  :disabled="savingReviewed"
                  @click="setReviewed(false)"
                >
                  {{ $t("review.unmarkReviewed") }}
                </button>
              </template>
              <button
                v-else
                type="button"
                class="xbtn xbtn--outline"
                :disabled="savingReviewed"
                @click="setReviewed(true)"
              >
                <i class="mdi mdi-check" aria-hidden="true"></i>
                {{ $t("review.markReviewed") }}
              </button>
            </div>
          </section>

          <section class="xpaper">
            <exercise-sheet
              :exercise="view.exercise"
              mode="grade"
              :busy-item="busyItem"
              @correct="setCorrection"
            />
          </section>

          <section class="xcompose">
            <label for="xfeedback" class="xcompose__label">{{
              $t("review.yourComment")
            }}</label>
            <p class="xcompose__hint">
              {{ $t("review.commentHint") }}
            </p>
            <textarea
              id="xfeedback"
              v-model="feedbackText"
              class="xcompose__input"
              rows="5"
              maxlength="4000"
              :placeholder="$t('review.placeholder')"
            ></textarea>
            <div class="xcompose__foot">
              <span v-if="view.feedback" class="xcompose__status">
                {{
                  view.feedback.readAt
                    ? $t("review.readOn", {
                        date: formatDate(view.feedback.readAt),
                      })
                    : $t("review.notRead")
                }}
              </span>
              <button
                type="button"
                class="xbtn xbtn--primary"
                :disabled="
                  !feedbackText.trim() || savingFeedback || !feedbackChanged
                "
                @click="saveFeedback"
              >
                {{
                  savingFeedback
                    ? $t("review.sending")
                    : view.feedback
                    ? $t("review.update")
                    : $t("review.send")
                }}
              </button>
            </div>
          </section>
        </template>
      </template>
    </div>
  </div>
</template>

<script>
import partnerApi from "@/shared/pitchPartnerApi";
import ExerciseSheet from "@/components/exercises/ExerciseSheet.vue";
import ScoreMark from "@/components/exercises/ScoreMark.vue";
import { languageTag } from "@/plugins/i18n";

export default {
  name: "ExerciseAttemptReview",
  components: { ExerciseSheet, ScoreMark },

  data() {
    return {
      view: null,
      loading: true,
      error: null,
      busyItem: null,
      feedbackText: "",
      savingFeedback: false,
      savingReviewed: false,
    };
  },

  computed: {
    finished() {
      return this.view?.attempt?.status === "FINISHED";
    },
    toleranceCount() {
      return (this.view?.exercise?.items || []).filter(
        (item) =>
          item.result &&
          item.result.acceptedWithTolerance &&
          !item.result.overridden
      ).length;
    },
    feedbackChanged() {
      return (
        this.feedbackText.trim() !== (this.view?.feedback?.text || "").trim()
      );
    },
  },

  mounted() {
    this.load();
  },

  methods: {
    goBack() {
      if (window.history.length > 1) this.$router.back();
      else this.$router.push("/connect/professor/turmas");
    },

    apply(view) {
      this.view = view;
      if (!this.feedbackChanged || !this.feedbackText) {
        this.feedbackText = view?.feedback?.text || "";
      }
    },

    load() {
      this.loading = true;
      this.error = null;
      partnerApi
        .getExerciseAttempt(this.$route.params.id)
        .then((res) => this.apply(res.data?.data || null))
        .catch((err) => {
          this.error =
            err?.response?.data?.message === "Aluno nao encontrado."
              ? "review.notYours"
              : "review.openFailed";
        })
        .finally(() => {
          this.loading = false;
        });
    },

    setCorrection(itemId, correct) {
      this.busyItem = itemId;
      partnerApi
        .setExerciseCorrection(this.view.attempt.id, itemId, correct)
        .then((res) => this.apply(res.data?.data || this.view))
        .catch(() => this.alert(this.$t("review.correctionFailed"), 1))
        .finally(() => {
          this.busyItem = null;
        });
    },

    setReviewed(reviewed) {
      this.savingReviewed = true;
      partnerApi
        .setExerciseReviewed(this.view.attempt.id, reviewed)
        .then((res) => this.apply(res.data?.data || this.view))
        .catch(() => this.alert(this.$t("review.reviewedFailed"), 1))
        .finally(() => {
          this.savingReviewed = false;
        });
    },

    saveFeedback() {
      this.savingFeedback = true;
      const isUpdate = !!this.view.feedback;
      partnerApi
        .saveExerciseFeedback(this.view.attempt.id, this.feedbackText)
        .then((res) => {
          this.view = res.data?.data || this.view;
          this.feedbackText = this.view?.feedback?.text || this.feedbackText;
          this.alert(
            isUpdate ? this.$t("review.updated") : this.$t("review.sent")
          );
        })
        .catch((err) =>
          this.alert(
            err?.response?.data?.message || this.$t("review.sendFailed"),
            1
          )
        )
        .finally(() => {
          this.savingFeedback = false;
        });
    },

    formatDate(value) {
      if (!value) return "";
      return new Date(value).toLocaleString(languageTag(), {
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    alert(text, type = 0) {
      this.$root.$emit("display-alert", { text, type });
    },
  },
};
</script>

<style lang="scss" scoped>
.xrev {
  --ink: var(--dk-ink, #2b2540);
  --muted: var(--dk-muted, #7a7590);
  --wash: var(--dk-wash, #f6f2fb);
  --rule: var(--dk-line, #ece6f5);
  --fill: var(--dk-grape, #a826e1);
  /* Retorno de 16/10: a caneta do professor e roxa (paleta do professor). */
  --pen: var(--dk-grape, #a826e1);

  min-height: 100%;
  background: var(--wash);
  padding: clamp(20px, 4vw, 44px) 16px 80px;
  color: var(--ink);
}

.xrev__inner {
  max-width: 820px;
  margin: 0 auto;
}

.xback {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 0;
  background: none;
  padding: 6px 0;
  font: inherit;
  font-size: 14px;
  font-weight: 500;
  color: var(--muted);
  cursor: pointer;
}

.xback:hover,
.xback:focus-visible {
  color: var(--ink);
}

.xstate {
  display: flex;
  justify-content: center;
  padding: 80px 0;
}

.xstate__text {
  color: var(--muted);
}

.xhead {
  margin: 16px 0 20px;
}

.xhead__student {
  margin: 0 0 4px;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--fill);
}

.xhead__title {
  margin: 0;
  font-family: "Poppins", sans-serif;
  font-weight: 600;
  font-size: clamp(26px, 4vw, 34px);
  line-height: 1.15;
}

.xhead__ex {
  font-family: $body-font-family;
  font-size: 0.55em;
  font-weight: 500;
  color: var(--muted);
}

.xhead__prompt {
  margin: 12px 0 0;
  font-size: 16px;
  font-weight: 600;
}

.xwait,
.xresult,
.xcompose {
  padding: 18px 22px;
  border-radius: 16px;
  background: var(--dk-surface, #fff);
  border: 1px solid var(--rule);
}

.xwait__title {
  margin: 0;
  font-weight: 600;
}

.xwait__text {
  margin: 4px 0 0;
  font-size: 14px;
  color: var(--muted);
}

.xresult {
  display: flex;
  align-items: center;
  gap: 18px;
  margin-bottom: 16px;
}

.xresult__lead {
  margin: 0;
  font-size: 17px;
  font-weight: 600;
}

.xresult__hint {
  margin: 4px 0 0;
  font-size: 13.5px;
  line-height: 1.5;
  color: var(--muted);
}

.xpaper {
  padding: 8px clamp(18px, 4vw, 36px);
  border-radius: 16px;
  background: var(--dk-surface, #fff);
  border: 1px solid var(--rule);
  box-shadow: 0 1px 0 var(--dk-line, #e6def2),
    0 12px 32px -24px rgba(43, 37, 64, 0.35);
}

.xcompose {
  margin-top: 18px;
  border-left: 4px solid var(--pen);
}

.xcompose__label {
  display: block;
  font-family: "Poppins", sans-serif;
  font-weight: 600;
  font-size: 16px;
  line-height: 1.3;
  color: var(--pen);
}

.xcompose__hint {
  margin: 2px 0 10px;
  font-size: 13px;
  color: var(--muted);
}

.xcompose__input {
  width: 100%;
  padding: 12px 14px;
  border: 1.5px solid var(--dk-line-strong, #ded7ea);
  border-radius: 12px;
  font: inherit;
  font-size: 15px;
  line-height: 1.6;
  color: var(--ink);
  resize: vertical;
  outline: none;
}

.xcompose__input:focus {
  border-color: var(--pen);
  box-shadow: 0 0 0 3px rgba(168, 38, 225, 0.12);
}

.xcompose__foot {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 10px 16px;
  margin-top: 12px;
}

.xcompose__status {
  margin-right: auto;
  font-size: 12.5px;
  color: var(--muted);
}

.xbtn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;
  padding: 0 22px;
  border-radius: 999px;
  border: 0;
  font: inherit;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
}

.xbtn:focus-visible {
  outline: 3px solid rgba(168, 38, 225, 0.35);
  outline-offset: 2px;
}

.xbtn--primary {
  background: var(--fill);
  color: #fff;
}

.xbtn--outline {
  gap: 6px;
  min-height: 40px;
  padding: 0 18px;
  background: var(--dk-surface, #fff);
  border: 1.5px solid var(--fill);
  color: var(--fill);
  font-size: 14px;
}

.xbtn--outline:disabled {
  opacity: 0.5;
  cursor: default;
}

.xreviewed {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 12px;
  margin-left: auto;
}

.xreviewed__done {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 600;
  color: var(--dk-teal, #00a379);
}

.xlink {
  border: 0;
  background: none;
  padding: 0;
  font: inherit;
  font-size: 13px;
  color: var(--muted);
  text-decoration: underline;
  cursor: pointer;
}

.xbtn--primary:disabled {
  opacity: 0.5;
  cursor: default;
}

@media (max-width: 600px) {
  .xresult {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
  }

  .xreviewed {
    margin: 8px 0 0;
  }
}
</style>
